import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { Sunflower } from "@/components/ArthaLogo";
import { DemoCTA } from "@/components/DemoCTA";
import { ArrowUpRight, Compass, FileCheck2, Globe2, ShieldCheck, Sparkles, Users2 } from "lucide-react";

const COUNTRIES_TEASER = [
  { flag: "🇮🇳", name: "India", tag: "Pvt Ltd · LLP" },
  { flag: "🇺🇸", name: "United States", tag: "Delaware LLC · C-Corp" },
  { flag: "🇬🇧", name: "United Kingdom", tag: "Ltd · 72-hour filing" },
  { flag: "🇦🇪", name: "UAE", tag: "IFZA Free Zone" },
  { flag: "🇸🇬", name: "Singapore", tag: "Pte Ltd" },
  { flag: "🇪🇪", name: "Estonia", tag: "e-Residency OÜ" },
  { flag: "🇨🇭", name: "Switzerland", tag: "GmbH · AG" },
];

const FEATURES = [
  { icon: Globe2, title: "Seven jurisdictions, one interface",
    body: "Incorporate in India, the US, UK, UAE, Singapore, Estonia or Switzerland without stitching together local lawyers, portals, and registrars." },
  { icon: ShieldCheck, title: "A compliance engine that never sleeps",
    body: "Annual filings, director KYC, corporate tax, VAT/GST and audit triggers are scheduled the minute your entity is born." },
  { icon: Users2, title: "A vetted partner, everywhere",
    body: "Company secretaries, registered agents, and tax counsel — routed by geography, rated by outcomes." },
  { icon: Sparkles, title: "AI that actually reads the forms",
    body: "Claude-grade reasoning assists with jurisdiction selection, form filling, and compliance questions in plain English." },
];

const Landing = () => (
  <div className="bg-surface-app min-h-screen text-ink-body">
    <MarketingNav />
    {/* HERO */}
    <section className="relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-20">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-3 mb-10 fade-up">
              <span className="inline-block w-2 h-2 rounded-full bg-artha-amber" />
              <span className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent">
                Mahakali Tribunal · Business Creation Layer
              </span>
            </div>
            <h1 className="h-display text-[56px] md:text-[88px] lg:text-[108px] fade-up delay-1">
              Incorporate<br />
              <span className="italic text-artha-amber">anywhere.</span><br />
              Stay compliant <br className="hidden md:block" />
              <span className="italic">everywhere.</span>
            </h1>
            <p className="mt-10 text-lg md:text-xl text-ink-muted max-w-xl leading-relaxed fade-up delay-2">
              Artha is a single operating system for global business registration and compliance.
              Pick a jurisdiction, answer a few questions, pay once — and let a verified local partner get your company live.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3 fade-up delay-3">
              <Link to="/signup" data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-2 bg-artha-ink text-artha-cream px-7 py-4 text-sm tracking-tight hover:bg-artha-amber hover:text-ink-body transition-all duration-300">
                Start an incorporation
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <DemoCTA variant="secondary" label="Try live demo" testId="hero-demo-cta" className="px-7 py-4" />
              <Link to="/countries" data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 px-7 py-4 text-sm tracking-tight border border-artha-ink/20 hover:border-artha-ink transition-colors">
                Browse jurisdictions
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-8 text-xs text-ink-muted fade-up delay-4">
              <Stat number="7" label="Jurisdictions" />
              <Stat number="24h" label="Fastest filing" />
              <Stat number="180+" label="Compliance rules tracked" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4 relative h-full min-h-[280px] hidden lg:flex items-end justify-end">
            <div className="relative">
              <div className="absolute -top-8 -right-8 w-72 h-72 bg-artha-amber" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)" }} />
              <div className="relative z-10 p-10">
                <Sunflower size={260} />
              </div>
              <div className="absolute -bottom-4 right-4 text-[10px] uppercase tracking-[0.3em] text-ink-muted font-mono-accent">
                A000.01 — Sunflower Mark
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* marquee band */}
      <div className="border-y border-border-subtle overflow-hidden bg-surface-card">
        <div className="flex whitespace-nowrap py-5 marquee-track">
          {[...COUNTRIES_TEASER, ...COUNTRIES_TEASER].map((c, i) => (
            <div key={i} className="flex items-center gap-3 px-8 text-ink-muted">
              <span className="text-2xl">{c.flag}</span>
              <span className="text-sm tracking-tight">{c.name}</span>
              <span className="text-xs font-mono-accent uppercase tracking-[0.2em] text-artha-amber">{c.tag}</span>
              <span className="text-artha-light-border">•</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* VALUE PROP */}
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24">
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent mb-6">
            Why Artha
          </div>
          <h2 className="h-display text-4xl md:text-5xl lg:text-6xl">
            The paperwork of <span className="italic">seven countries</span>, compressed into a weekend.
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-artha-light-border border border-border-subtle">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-surface-app p-8 hover:bg-surface-card transition-colors duration-300" data-testid={`feature-${i}`}>
              <f.icon size={22} className="text-artha-amber mb-5" strokeWidth={1.25} />
              <h3 className="text-xl tracking-tight mb-3">{f.title}</h3>
              <p className="text-sm text-ink-muted leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* COUNTRIES */}
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-20 border-t border-border-subtle">
      <div className="flex justify-between items-end mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent mb-4">
            Jurisdictions
          </div>
          <h2 className="h-display text-4xl md:text-5xl">Seven doors to the global economy.</h2>
        </div>
        <Link to="/countries" className="amber-link text-sm hidden md:inline-block">View all →</Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-artha-light-border border border-border-subtle">
        {COUNTRIES_TEASER.map((c, i) => (
          <Link to="/countries" key={i} data-testid={`country-card-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
            className="bg-surface-app p-8 hover:bg-surface-card transition-colors group">
            <div className="text-3xl mb-6">{c.flag}</div>
            <div className="text-xl tracking-tight mb-1">{c.name}</div>
            <div className="text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted">{c.tag}</div>
            <div className="mt-6 text-sm text-ink-muted group-hover:text-artha-amber transition-colors inline-flex items-center gap-1">
              Explore <ArrowUpRight size={14} />
            </div>
          </Link>
        ))}
        <div className="bg-artha-ink text-artha-cream p-8 flex flex-col justify-between">
          <div className="text-xs uppercase tracking-[0.28em] text-artha-amber font-mono-accent">More soon</div>
          <div className="font-serif-display text-4xl">Global<br />roll-out.</div>
        </div>
      </div>
    </section>

    {/* HOW IT WORKS */}
    <section className="bg-artha-ink text-artha-cream">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.28em] text-artha-amber font-mono-accent mb-6">
              Four steps
            </div>
            <h2 className="h-display text-4xl md:text-5xl">
              From <span className="italic text-artha-amber">idea</span> to incorporated.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-10 mt-4">
            {[
              { n: "01", t: "Pick a jurisdiction", d: "Let Artha's AI advisor recommend the right country and entity based on your tax, funding and market goals." },
              { n: "02", t: "Answer & pay", d: "Fill a guided form, pay the flat fee, and upload your IDs. No back-and-forth emails." },
              { n: "03", t: "Routed to a partner", d: "A verified local company secretary or registered agent picks up your file and files it on your behalf." },
              { n: "04", t: "Live + compliant", d: "Receive your certificate, and watch your compliance calendar auto-populate with every filing you owe." },
            ].map((s) => (
              <div key={s.n} className="border-t border-white/15 pt-6">
                <div className="font-mono-accent text-artha-amber text-xs tracking-[0.3em]">{s.n}</div>
                <div className="h-display text-2xl md:text-3xl mt-3">{s.t}</div>
                <p className="text-sm text-white/60 mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ECOSYSTEM */}
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24">
      <div className="grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.28em] text-ink-muted font-mono-accent mb-6">
            The ecosystem
          </div>
          <h2 className="h-display text-4xl md:text-5xl">Artha is the first door. <span className="italic">Not the only room.</span></h2>
          <p className="mt-6 text-ink-muted max-w-md leading-relaxed">
            The moment your company exists, it inherits the rest of the Mahakali Tribunal stack — capital formation, financial ops, legal execution, and cybersecurity.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-artha-light-border border border-border-subtle">
          {[
            { name: "Sri", role: "Capital formation" },
            { name: "Shrika", role: "Financial operations" },
            { name: "Marianne", role: "Legal execution" },
            { name: "Nyaya Kosa", role: "Law infrastructure" },
            { name: "Clarke", role: "Cybersecurity" },
            { name: "Tribe", role: "Identity" },
          ].map((p) => (
            <div key={p.name} className="bg-surface-app p-6 hover:bg-surface-card transition-colors">
              <div className="h-display text-2xl">{p.name}</div>
              <div className="text-xs font-mono-accent uppercase tracking-[0.2em] text-ink-muted mt-1">{p.role}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="border-t border-border-subtle">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid grid-cols-12 gap-8 items-center">
        <div className="col-span-12 lg:col-span-8">
          <h2 className="h-display text-5xl md:text-7xl">
            Your company is <span className="italic text-artha-amber">three clicks</span><br />from existing.
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-4 flex flex-col gap-3 lg:items-end">
          <Link to="/signup" data-testid="final-cta-signup"
            className="inline-flex items-center justify-between gap-3 bg-artha-amber text-ink-body px-7 py-5 text-sm tracking-tight hover:bg-artha-ink hover:text-artha-cream transition-colors w-full lg:w-auto">
            Create your Artha account <ArrowUpRight size={16} />
          </Link>
          <Link to="/pricing" className="text-sm amber-link">View pricing →</Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

const Stat = ({ number, label }) => (
  <div>
    <div className="h-display text-3xl">{number}</div>
    <div className="text-xs font-mono-accent uppercase tracking-[0.2em] mt-1">{label}</div>
  </div>
);

export default Landing;
